import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { GT_ZERO, INTEGER, REQUIRED } from 'utils/helpers/validationMessages';
import { isNumberWithOptionalFormatting } from 'utils/helpers/formValidations';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';

import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import AssociatedShipmentsGrid from '../AssociatedShipmentsGrid';
import CreateShipmentWizard from '../shipments/create/CreateShipmentWizard';
import NoAssociatedCard from '../components/NoAssociatedCard';

import { useCancelShipmentMutation, useGetLocationsQuery } from '../batchesEndpoints';
import { initialShipmentState } from '../create/initialState';

const ViewAssociatedShipments = React.forwardRef((props, ref) => {
  const {
    availableDate,
    batchId,
    dataCompanyId,
    batchOwner,
    onEditPage,
    originId,
    originName,
    productId,
    refetchBatchData,
    shipments,
    volume,
    agreementCode,
    handleBatchSave,
    isFetchingShipments,
    selectedDistribution,
    setSelectedDistribution,
    selectedShipment,
    isEditPermission,
    setHasNewShipments,
    setNewShipmentsValidated,
    setSelectedShipment,
    setShipmentsEdited,
    setEditedTotalVolume,
    associatedShipments,
    setAssociatedShipments,
    updatedRows,
    setUpdatedRows,
    setInvalidAgreementCode,
    setInvalidShipmentVolume,
  } = props;

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const dispatch = useDispatch();
  const [createShipment, setCreateShipment] = useState(false);
  const handleCloseCreate = R.compose(setCreateShipment, R.F);
  const [isExplicitDialogOpen, setIsExplicitDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [idToCancel, setIdToCancel] = useState(null);
  const [isOnlyShipment, setIsOnlyShipment] = useState(false);
  const [cancelShipment] = useCancelShipmentMutation();
  const [isNewShipmentVoumeDilogOpen, setIsNewShipmentVoumeDilogOpen] = useState(false);

  const inventoryOwner = {
    companyCustomerIdName: R.prop('batchOwnerIdn', batchOwner),
    dataCompanyId,
  };

  const getLocationQuery = {
    params: {
      bookingOriginId: originId,
      dataCompanyId,
      inventoryOwner,
      productId,
    },
  };

  const { data: locationsQuery = [] } = useGetLocationsQuery(getLocationQuery);

  const validateVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [R.gt(1), R.always(GT_ZERO)],
    [val => !isNumberWithOptionalFormatting(val), R.always(INTEGER)],
    [R.T, R.always('')],
  ]);

  // validation for new shipments
  const hasNewShipments = R.any(R.propEq('isNew', true), associatedShipments);

  useEffect(() => {
    setShipmentsEdited(updatedRows.length > 0);
    setEditedTotalVolume(
      R.pipe(
        R.filter(s => s.status !== 'Canceled' && s.status !== 'CANCELED'),
        R.pluck('volume'),
        R.sum
      )(associatedShipments)
    );

    setNewShipmentsValidated(
      hasNewShipments &&
        R.all(
          R.identity,
          R.map(
            row =>
              R.all(R.identity, [
                R.isEmpty(validateVolume(R.prop('volume', row))),
                R.prop('shipper', row),
                R.prop('bookingOriginId', row),
              ]),
            R.filter(R.prop('isNew'), associatedShipments)
          )
        )
    );

    setHasNewShipments(hasNewShipments);
  }, [
    associatedShipments,
    hasNewShipments,
    setEditedTotalVolume,
    setHasNewShipments,
    setNewShipmentsValidated,
    setShipmentsEdited,
    updatedRows.length,
    validateVolume,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAddNewShipment = useCallback(() => {
    setAssociatedShipments(prev =>
      R.append(
        {
          ...initialShipmentState,
          bookingOriginId: originId,
          bookingOriginName: originName,
          isNew: true,
          locationId: R.prop('id', R.find(R.prop('isDefaultLocation'), locationsQuery)),
          shipmentId: prev.length + 1,
          shipperName: '',
          status: '',
          volume: 0,
        },
        prev
      )
    );

    // scroll to the bottom of the page after datagrid view has updated
    setTimeout(() => {
      window.scrollTo(0, document.body.scrollHeight);
    }, 500);
  });

  // Keyboard shortcut handler for Ctrl + A to add new shipment and Ctrl + Enter to save
  const handleKeyDown = useCallback(
    event => {
      if (
        event.ctrlKey &&
        event.key === 'a' &&
        isEditPermission &&
        !R.isEmpty(associatedShipments)
      ) {
        event.preventDefault();
        handleAddNewShipment();
      } else if (
        event.ctrlKey &&
        event.key === 'Enter' &&
        isEditPermission &&
        (updatedRows.length > 0 || hasNewShipments)
      ) {
        event.preventDefault();
        handleBatchSave();
      }
    },
    [
      isEditPermission,
      associatedShipments,
      handleAddNewShipment,
      updatedRows.length,
      hasNewShipments,
      handleBatchSave,
    ]
  );

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const getTotalShipmentsVolume = withoutNewShipments => {
    const filteredShipments = R.pipe(
      // First filter out canceled shipments
      R.filter(s => s.status !== 'Canceled' && s.status !== 'CANCELED'),
      // Then optionally filter out new shipments if requested
      withoutNewShipments ? R.reject(R.propEq('isNew', true)) : R.identity
    )(associatedShipments);

    return R.sum(R.pluck('volume', filteredShipments));
  };
  const cancelSelectedShipment = () => {
    cancelShipment({ body: { shipmentId: idToCancel } })
      .then(res => {
        refetchBatchData();
        setIsExplicitDialogOpen(false);
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
        }
      })
      .finally(() =>
        setTimeout(() => {
          setIdToCancel(null);
          setDialogType('');
          setIsOnlyShipment(false);
        }, 500)
      );
  };

  const handleCancel = (type, cancelId) => {
    setDialogType(type);
    setIdToCancel(cancelId);
    setIsExplicitDialogOpen(true);
  };

  const handleShipmentCancel = (type, cancelId, isTheOnlyShipment) => {
    handleCancel(type, cancelId);
    setIsOnlyShipment(isTheOnlyShipment);
  };

  const warningList = [
    'This will also cancel all associated distributions.',
    'This action cannot be undone.',
  ];
  const additionalWarnings = [
    'You are removing the last shipment in this batch.',
    'This will make the Batch invalid until at least one shipment is added.',
  ];

  useImperativeHandle(ref, () => ({
    handleAddNewShipment,
    handleShowVolumeConfirmation: () => setIsNewShipmentVoumeDilogOpen(true),
  }));

  return (
    <Box>
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography sx={{ mb: { sm: 0, xs: 2 }, pr: 3 }} variant="h5">
          Associated Shipments
        </Typography>
      </Stack>
      {isFetchingShipments ? (
        <Box textAlign="center" width="100%">
          <CircularProgress size={48} />
        </Box>
      ) : R.isEmpty(associatedShipments) ? (
        <NoAssociatedCard type="shipment" />
      ) : (
        <>
          <AssociatedShipmentsGrid
            associatedShipments={associatedShipments}
            availableDate={availableDate}
            batchId={batchId}
            batchOwnerName={batchOwner.batchOwnerName}
            dataCompanyId={batchOwner.dataCompanyId}
            handleBatchSave={handleBatchSave}
            isEditPermission={isEditPermission}
            onCancel={handleShipmentCancel}
            onEditPage={onEditPage}
            originId={originId}
            originName={originName}
            productId={productId}
            selectedDistribution={selectedDistribution}
            selectedShipment={selectedShipment}
            setAssociatedShipments={setAssociatedShipments}
            setInvalidAgreementCode={setInvalidAgreementCode}
            setInvalidShipmentVolume={setInvalidShipmentVolume}
            setSelectedDistribution={setSelectedDistribution}
            setSelectedShipment={setSelectedShipment}
            setShipmentsEdited={setShipmentsEdited}
            setUpdatedRows={setUpdatedRows}
            shipments={shipments}
          />
        </>
      )}
      <DialogExplicitConfirmation
        confirmationMessage={`If you still want to cancel the ${dialogType}, enter the word below and click Confirm.`}
        confirmationValue="CANCEL"
        dialogOpen={isExplicitDialogOpen}
        handleCloseDialog={() => {
          setIsExplicitDialogOpen(false);
          setTimeout(() => {
            setDialogType('');
            setIdToCancel(null);
          }, 600);
        }}
        label={`Type "CANCEL"`}
        onConfirm={cancelSelectedShipment}
        title={`Are you sure you want to cancel this ${dialogType}?`}
        warningList={!isOnlyShipment ? warningList : R.concat(additionalWarnings, warningList)}
      />
      {createShipment && (
        <CreateShipmentWizard
          agreementCode={agreementCode}
          availableDate={availableDate}
          batchOwnerName={batchOwner.batchOwnerName}
          batchVolume={volume}
          dataCompanyId={batchOwner.dataCompanyId}
          onClose={handleCloseCreate}
          originId={originId}
          originName={originName}
          productId={productId}
          refetchBatchData={refetchBatchData}
        />
      )}
      <Dialog
        maxWidth="xs"
        onClose={() => setIsNewShipmentVoumeDilogOpen(false)}
        open={isNewShipmentVoumeDilogOpen}
        slotProps={{
          paper: { elevation: 10 },
        }}
        sx={{ '& .MuiDialog-paper': { width: 380 } }}
      >
        <DialogTitle>
          You are about to add a new shipment and increase the total Batch volume.
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ display: 'inline' }} variant="body1bold">
            Current volume
          </Typography>
          : {formatNumberWithoutDecimals(getTotalShipmentsVolume(true) ?? 0)} bbl
          <br />
          <br />
          <Typography sx={{ display: 'inline' }} variant="body1bold">
            Volume after confirm
          </Typography>
          : {formatNumberWithoutDecimals(getTotalShipmentsVolume() ?? 0)} bbl
        </DialogContent>
        <Divider />
        <DialogContent sx={{ textAlign: 'center' }}>
          If you still want to increase the total batch volume, click confirm
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsNewShipmentVoumeDilogOpen(false)} variant="outlined">
            Back
          </Button>
          <Button
            autoFocus
            onClick={() => {
              setIsNewShipmentVoumeDilogOpen(false);
              handleBatchSave();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

ViewAssociatedShipments.propTypes = {
  agreementCode: PropTypes.string,
  associatedShipments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  availableDate: PropTypes.string.isRequired,
  batchId: PropTypes.number.isRequired,
  batchOwner: PropTypes.shape({
    batchOwnerName: PropTypes.string,
    dataCompanyId: PropTypes.number,
  }).isRequired,
  dataCompanyId: PropTypes.number.isRequired,
  handleBatchSave: PropTypes.func.isRequired,
  isEditPermission: PropTypes.bool.isRequired,
  isFetchingShipments: PropTypes.bool.isRequired,
  onEditPage: PropTypes.bool.isRequired,
  originId: PropTypes.number.isRequired,
  originName: PropTypes.string.isRequired,
  productId: PropTypes.number.isRequired,
  refetchBatchData: PropTypes.func.isRequired,
  selectedDistribution: PropTypes.shape({}),
  selectedShipment: PropTypes.shape({}),
  setAssociatedShipments: PropTypes.func.isRequired,
  setEditedTotalVolume: PropTypes.func.isRequired,
  setHasNewShipments: PropTypes.func.isRequired,
  setInvalidAgreementCode: PropTypes.func.isRequired,
  setInvalidShipmentVolume: PropTypes.func.isRequired,
  setNewShipmentsValidated: PropTypes.func.isRequired,
  setSelectedDistribution: PropTypes.func.isRequired,
  setSelectedShipment: PropTypes.func.isRequired,
  setShipmentsEdited: PropTypes.func.isRequired,
  setUpdatedRows: PropTypes.func.isRequired,
  shipments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updatedRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  volume: PropTypes.number.isRequired,
};
ViewAssociatedShipments.defaultProps = {
  agreementCode: null,
  selectedDistribution: null,
  selectedShipment: null,
};

export default ViewAssociatedShipments;
